/**
 * Console logging for the meeting booking flows.
 *
 * Two booking solutions run side by side — the old one (Litium time slots +
 * links to /boka-gratis-mote/...) and the new one (the meeting booking API
 * behind /api/publicmeetingbooking/*). When booking only works for some
 * retailers the first questions are always: which solution did this retailer
 * get, why, and what did the API answer? This module puts that in the console.
 *
 * Enable:  add ?meetingdebug=1 to the url — it sticks for the rest of the tab session
 * Disable: ?meetingdebug=0, or run meetingBookingDebug(false) in the console
 */

const STORAGE_KEY = 'meeting-booking-debug';
const PREFIX = '[MeetingBooking]';

let enabled: boolean | null = null;

function readQueryFlag(): string | null {
    const match = /[?&]meetingdebug=([^&#]*)/i.exec(window.location.search);
    return match ? decodeURIComponent(match[1]) : null;
}

function readStoredFlag(): boolean {
    try {
        return window.sessionStorage.getItem(STORAGE_KEY) === '1';
    } catch {
        return false;
    }
}

function storeFlag(value: boolean): void {
    try {
        if (value) {
            window.sessionStorage.setItem(STORAGE_KEY, '1');
        } else {
            window.sessionStorage.removeItem(STORAGE_KEY);
        }
    } catch {
        // Private mode — the flag then only lives for this page view.
    }
}

export function isMeetingDebugEnabled(): boolean {
    if (enabled === null) {
        const queryFlag = readQueryFlag();
        if (queryFlag === null) {
            enabled = readStoredFlag();
        } else {
            enabled = queryFlag !== '0' && queryFlag.toLowerCase() !== 'false';
            storeFlag(enabled);
        }
    }
    return enabled;
}

export function setMeetingDebugEnabled(value: boolean): void {
    enabled = value;
    storeFlag(value);
    console.log(PREFIX, value ? 'logging enabled' : 'logging disabled');
}

export function meetingLog(scope: string, message: string, data?: any): void {
    if (!isMeetingDebugEnabled()) return;
    if (data === undefined) {
        console.log(PREFIX + ' ' + scope + ': ' + message);
    } else {
        console.log(PREFIX + ' ' + scope + ': ' + message, data);
    }
}

export function meetingWarn(scope: string, message: string, data?: any): void {
    if (!isMeetingDebugEnabled()) return;
    if (data === undefined) {
        console.warn(PREFIX + ' ' + scope + ': ' + message);
    } else {
        console.warn(PREFIX + ' ' + scope + ': ' + message, data);
    }
}

export function meetingTable(scope: string, message: string, rows: any[]): void {
    if (!isMeetingDebugEnabled()) return;
    console.log(PREFIX + ' ' + scope + ': ' + message);
    if (typeof console.table === 'function') {
        console.table(rows);
    } else {
        console.log(rows);
    }
}

export interface MeetingSolutionInfo {
    /** The flag the component actually acts on. */
    useNewMeetingBooking: boolean;
    /** Where that flag came from, when the component knows. */
    source?: string;
    retailerName?: string | null;
    organizationSystemId?: string | null;
    availableMeetingTypes?: string[] | null;
    bookMeetingUrlStore?: string | null;
    bookMeetingUrlHome?: string | null;
    bookMeetingUrlVideo?: string | null;
    [key: string]: any;
}

/** Logs which of the two booking solutions a retailer ended up on, and on what data. */
export function logMeetingSolution(scope: string, info: MeetingSolutionInfo): void {
    if (!isMeetingDebugEnabled()) return;
    const solution = info.useNewMeetingBooking
        ? 'NEW booking (meeting booking api)'
        : 'OLD booking (litium slots + links)';
    console.log(PREFIX + ' ' + scope + ': ' + solution, info);
}

function describeBody(body: any): any {
    if (typeof body !== 'string') {
        return body instanceof FormData ? '<FormData>' : body;
    }
    try {
        return JSON.parse(body);
    } catch {
        return body;
    }
}

/**
 * fetch() that logs request and response when debugging is on, and is a plain
 * fetch() otherwise. Returns the untouched response, so callers read the body
 * exactly as before.
 */
export async function meetingFetch(scope: string, url: string, init?: RequestInit): Promise<Response> {
    if (!isMeetingDebugEnabled()) {
        return fetch(url, init);
    }

    const method = (init && init.method) || 'GET';
    const startedAt = Date.now();

    if (init && init.body !== undefined && init.body !== null) {
        console.log(PREFIX + ' ' + scope + ': ' + method + ' ' + url + ' — request', describeBody(init.body));
    }

    let response: Response;
    try {
        response = await fetch(url, init);
    } catch (e) {
        console.error(PREFIX + ' ' + scope + ': ' + method + ' ' + url + ' — request failed (network/cors)', e);
        throw e;
    }

    const elapsed = Date.now() - startedAt;
    let body: any;
    try {
        const text = await response.clone().text();
        try {
            body = JSON.parse(text);
        } catch {
            body = text;
        }
    } catch {
        body = '<response body could not be read>';
    }

    const line = PREFIX + ' ' + scope + ': ' + method + ' ' + url +
        ' -> ' + response.status + ' ' + response.statusText + ' (' + elapsed + ' ms)';
    if (response.ok) {
        console.log(line, body);
    } else {
        console.warn(line, body);
    }

    return response;
}

export type MeetingTrackEvent =
    | 'started'
    | 'slot_selected'
    | 'completed'
    | 'abandoned'
    | 'bankid_started'
    | 'bankid_cancelled'
    | 'bankid_failed'
    | 'availability_failed'
    | 'booking_failed'
    | 'images_failed';

export interface MeetingTrackData {
    /** Random id per opened booking flow — ties one customer's events together in the log. */
    flowId: string;
    /** Where on the site the flow was opened from (header-menu, mega-menu-kitchen, retailer-page-button ...). */
    source?: string | null;
    meetingType?: string | null;
    organizationId?: string | null;
    step?: string | null;
    meetingId?: string | null;
    message?: string | null;
    elapsedMs?: number;
}

const TRACK_URL = '/api/publicmeetingbooking/event';

export function createMeetingFlowId(): string {
    return Date.now().toString(36) + '-' + Math.random().toString(36).substring(2, 10);
}

/**
 * Reports a booking flow event to the server log (MeetingBooking.log) — unlike the
 * functions above it always runs, debug flag or not. Never put customer details
 * (name, email, phone, address) in here.
 *
 * Sent with sendBeacon so it survives the tab being closed. The body goes as
 * text/plain, which sendBeacon accepts in every browser; the server reads it as json.
 */
export function meetingTrack(event: MeetingTrackEvent, data: MeetingTrackData): void {
    const body = JSON.stringify({ ...data, event, page: window.location.pathname });
    meetingLog('track', event, data);

    try {
        if (navigator.sendBeacon && navigator.sendBeacon(TRACK_URL, new Blob([body], { type: 'text/plain' }))) {
            return;
        }
    } catch {
        // Fall through to fetch.
    }

    try {
        fetch(TRACK_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'text/plain' },
            body,
            keepalive: true
        }).catch(() => { /* logging must never break the booking */ });
    } catch {
        // Logging must never break the booking.
    }
}

// Lets you flip logging on/off from the console without reloading with a query string.
(window as any).meetingBookingDebug = function(value?: boolean): boolean {
    if (value !== undefined) {
        setMeetingDebugEnabled(value);
    }
    return isMeetingDebugEnabled();
};
